import {useState, type ReactNode} from 'react';
import {cn} from '../lib/cn';
import {Carousel} from '../carousel';

export type VerticalTabsStep = {
  title: ReactNode;
  text: ReactNode;
  images?: string[];
};

export type VerticalTabsProps = {
  steps: VerticalTabsStep[];
  stepKey?: string;
  getStepLabel?: (index: number) => ReactNode;
  className?: string;
};

function getKey(index: number, key?: string) {
  return `${key || 'step'}_${index}`;
}

export function VerticalTabs({
  steps,
  stepKey = '',
  getStepLabel = index => `Step ${index + 1}`,
  className,
}: VerticalTabsProps) {
  const defaultValue = getKey(0, stepKey);
  const [value, setValue] = useState(defaultValue);
  const hasImages = steps.every(step => step.images?.length);

  return (
    <div
      className={cn(
        'grid w-full items-stretch',
        hasImages ? 'grid-cols-[369px_1fr]' : 'grid-cols-1',
        className,
      )}
    >
      <div className="flex w-full flex-grow-0 flex-col flex-nowrap gap-0">
        {steps.map(({title, text}, index) => {
          const stepValue = getKey(index, stepKey);
          const active = value === stepValue;

          return (
            <button
              key={stepValue}
              type="button"
              onClick={() => setValue(stepValue)}
              className={cn(
                'box-border flex w-full flex-none flex-col items-start gap-1 border-b bg-[var(--vertical-tabs-step-bg)] p-6 pb-8 text-left transition-[height] duration-300',
                'border-b-[var(--vertical-tabs-step-border)] border-l-2 border-l-transparent',
                index === steps.length - 1 && 'border-b-transparent',
                active &&
                  'border-b-[var(--vertical-tabs-step-border)] border-l-[var(--vertical-tabs-active-border)] pb-6',
              )}
            >
              <span className="text-sm font-bold leading-4 text-[var(--vertical-tabs-step-label)]">
                {getStepLabel(index)}
              </span>
              <span
                className={cn(
                  'text-lg font-medium leading-[22px] text-[var(--vertical-tabs-title)] transition-[font-weight,line-height] duration-300',
                  active && 'font-bold leading-6',
                )}
              >
                {title}
              </span>
              <span
                className={cn(
                  'grid grid-rows-[0fr] overflow-hidden transition-[grid-template-rows] duration-300',
                  active && 'grid-rows-[1fr]',
                )}
              >
                <span
                  className={cn(
                    'min-h-0 text-sm font-medium leading-4 text-[var(--vertical-tabs-text)] opacity-0 transition duration-500',
                    'translate-y-2.5',
                    active && 'translate-y-0 opacity-100',
                  )}
                >
                  {text}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {hasImages &&
        steps.map(({images}, index) => {
          const stepValue = getKey(index, stepKey);

          if (value !== stepValue) {
            return null;
          }

          return (
            <div key={`${stepValue}_content`} className="h-full w-full">
              <Carousel>
                {images?.map(image => (
                  <img
                    key={image}
                    src={image}
                    className="h-full w-full object-contain"
                    alt=""
                  />
                ))}
              </Carousel>
            </div>
          );
        })}
    </div>
  );
}
